import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowDownLeft, ArrowUpRight, ChevronLeft, Gift } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import {
  fetchUserGiftTxns,
  fetchUserWallet,
  summarizeGifts,
  type AdminGiftSummary,
  type AdminWalletTxn,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { Skeleton } from '@/components/States'

/**
 * A3.16 — Sovg'a / Stars tranzaksiyalari. GET /admin/users/:id/wallet (backend YO'Q),
 * faqat sovg'a turlari (live_gift / live_gift_received). A3.15 = barcha to'lovlar;
 * bu ekran faqat sovg'a oqimini (yuborilgan/olingan) ajratib ko'rsatadi.
 */
export function UserGifts() {
  const t = useT()
  const { id = '' } = useParams()
  const [summary, setSummary] = useState<AdminGiftSummary | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    fetchUserWallet(Number(id), ac.signal)
      .then((w) => {
        if (alive) setSummary(summarizeGifts(w?.txns ?? []))
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id])

  const columns = useMemo<AdminColumn<AdminWalletTxn>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 160 },
      {
        key: 'delta',
        header: t.finance.amount,
        type: 'number',
        width: 130,
        align: 'right',
        render: (r) => (
          <span className={cn('block text-right font-mono tabular', r.delta >= 0 ? 'text-success' : 'text-danger')}>
            {r.delta >= 0 ? '+' : ''}
            {r.delta.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'kind',
        header: t.col.type,
        type: 'text',
        width: 150,
        render: (r) =>
          r.kind === 'live_gift_received' ? (
            <span className="inline-flex items-center gap-1 text-success">
              <ArrowDownLeft size={13} /> {t.userExtra.giftsReceived}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-danger">
              <ArrowUpRight size={13} /> {t.userExtra.giftsSent}
            </span>
          ),
      },
      { key: 'note', header: t.finance.note, type: 'longtext', minWidth: 180 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchUserGiftTxns(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.userExtra.giftsTitle}
        screenId="A3.16"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.users.cardTitle}
          </Link>
        }
      />
      <div className="flex flex-wrap gap-3 border-b border-border-subtle p-3">
        <GiftStat
          icon={<ArrowUpRight size={18} className="text-danger" />}
          label={t.userExtra.giftsSent}
          value={summary?.sent}
          loading={loading}
        />
        <GiftStat
          icon={<ArrowDownLeft size={18} className="text-success" />}
          label={t.userExtra.giftsReceived}
          value={summary?.received}
          loading={loading}
        />
        <GiftStat
          icon={<Gift size={18} className="text-accent" />}
          label={t.userExtra.giftsCount}
          value={summary?.count}
          loading={loading}
        />
      </div>
      <DataTable<AdminWalletTxn>
        screenId="A3.16"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Gift size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}

function GiftStat({
  icon,
  label,
  value,
  loading,
}: {
  icon: ReactNode
  label: string
  value: number | undefined
  loading: boolean
}) {
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        {loading ? (
          <Skeleton className="mt-1 h-6 w-16" />
        ) : (
          <div className="font-mono text-xl tabular text-primary">{(value ?? 0).toLocaleString()}</div>
        )}
      </div>
    </div>
  )
}
